// Use native array helpers; lodash imports removed to satisfy linter

import type { JsonBalanceExtended, JsonBalanceExtendedList } from 'src/store/shared/balances/types';
import type {
  JsonBalanceHistoryAsset,
  JsonBalanceHistoryExtended,
  JsonBalanceHistoryExtendedList,
} from 'src/store/shared/balance-history/types';

import { getTotalPerAsset, getTotalTooltip, getTotalValue } from '../calculations/totals';
import { getPricePerAsset } from '../calculations/prices';
import type {
  GetBalanceListParams,
  GetBalanceTreeDataParams,
  GetCompareListParams,
} from '../core/types';

// Local union type used for sorting/grouping operations in this file
type SortableBalanceItem =
  | (JsonBalanceExtended & { group?: string })
  | (JsonBalanceExtendedList & { group?: string })
  | (JsonBalanceHistoryExtended & { group?: string })
  | (JsonBalanceHistoryExtendedList & { group?: string })
  | {
      group?: string;
      key: string;
      label: string;
      assets: unknown[];
      children: unknown[];
    };

// Predicate: an account is considered 'active' (not a real error) when it either has
// visible assets or had all its assets removed by token filtering (isAllTokensFiltered).
// If the transformation already computed `hasError`, prefer that flag.
const isActiveAccount = (d: {
  assets: unknown[];
  isAllTokensFiltered?: boolean;
  hasError?: boolean;
}) =>
  typeof d.hasError === 'boolean' ? !d.hasError : d.assets.length > 0 || !!d.isAllTokensFiltered;

const getSortedBalanceAccountByGroup = <T extends { group?: string }>(data: T[]) =>
  data.sort((a, b) => {
    const ag = a.group ?? '';
    const bg = b.group ?? '';
    if (ag === 'Other') return 1;
    if (bg === 'Other') return -1;
    return ag.localeCompare(bg);
  });

export const getBalanceList = ({
  data = [],
  viewGroupBy = 'type',
  showError = true,
}: GetBalanceListParams): JsonBalanceExtendedList[] => {
  const result: JsonBalanceExtendedList[] = [];

  const filterData = showError ? data : data.filter(isActiveAccount);

  const groupedData = filterData.reduce<Record<string, JsonBalanceExtended[]>>((acc, cur) => {
    const key = String(cur.groupMap?.[viewGroupBy]);
    (acc[key] ||= []).push(cur);
    return acc;
  }, {});

  Object.entries(groupedData).forEach(([group, items]) => {
    result.push({
      key: `group:${group}`,
      label: group,
      assets: items.filter((item) => item.assets.length > 0).flatMap((el) => el.assets),
      isTotalDoubling: false,
      group,
      // required by JsonBalanceExtension
      groupMap: { [viewGroupBy]: group },
      hue: 0,
      piechartData: [],
      // group children placeholder
      children: [],
      groupAccountsId: items.map((el) => String(el.accountID)),
      // ensure JsonBalance fields
      accountID: '',
      accountName: '',
      isWallet: false,
      exchangeID: 0,
    });

    result.push(
      ...(items.map((item) => ({
        ...item,
        label: item.accountName,
        accountName: item.accountName || '',
        key: item.accountID,
        group,
        children: [],
        // filler to satisfy JsonBalanceExtendedList shape
        groupAccountsId: [],
      })) as JsonBalanceExtendedList[]),
    );
  });

  return getSortedBalanceAccountByGroup(result);
};

export const getCompareList = ({
  data = [],
  viewGroupBy = 'type',
  showError = true,
}: GetCompareListParams): JsonBalanceHistoryExtendedList[] => {
  const result: JsonBalanceHistoryExtendedList[] = [];

  const filterData = showError ? data : data.filter(isActiveAccount);

  const groupedData = filterData.reduce<Record<string, JsonBalanceHistoryExtended[]>>(
    (acc, cur) => {
      const key = String(cur.groupMap?.[viewGroupBy]);
      (acc[key] ||= []).push(cur);
      return acc;
    },
    {},
  );

  Object.entries(groupedData).forEach(([group, items]) => {
    result.push({
      key: `group:${group}`,
      label: group,
      assets: [] as JsonBalanceHistoryAsset[],
      group,
      groupMap: { [viewGroupBy]: group },
      hue: 0,
      piechartData: [],
      groupAccountsId: items.map((el) => String(el.accountID)),
      // ensure required JsonBalanceHistoryExtendedList fields
      accountID: '',
      accountName: '',
      isWallet: false,
      exchangeID: 0,
    });

    const mapped = items.map<JsonBalanceHistoryExtendedList>((it) => ({
      ...it,
      accountName: it.accountName ?? '',
      group,
      label: it.accountName || '',
      groupAccountsId: [String(it.accountID)],
    }));

    result.push(...mapped);
  });

  return getSortedBalanceAccountByGroup(result);
};

export function getBalanceTreeData({
  data = [],
  uniqTokens = [],
  viewGroupBy = 'type',
  isSmallBalancesVisible = false,
  selection = [],
  showError,
  excludeFutures = false,
}: GetBalanceTreeDataParams) {
  const filteredData = showError
    ? data
    : (data as (JsonBalanceExtended | JsonBalanceHistoryExtended)[]).filter(isActiveAccount);

  const groupedData = filteredData.reduce<
    Record<string, (JsonBalanceExtended | JsonBalanceHistoryExtended)[]>
  >((acc, cur) => {
    const key = String(cur.groupMap?.[viewGroupBy]);
    (acc[key] ||= []).push(cur);
    return acc;
  }, {});

  const sel = new Set(selection.map((s) => String(s)));

  const balanceTreeData = Object.entries(groupedData).map(
    ([group, children]: [string, (JsonBalanceExtended | JsonBalanceHistoryExtended)[]]) => {
      const matched = children.find((child) => sel.has(String(child.key)));
      const groupMap = matched?.groupMap;

      const subtotal = {
        key: `subtotal:${group}`,
        label: 'Subtotal',
        assets: uniqTokens.map((token) => {
          const volume = getTotalPerAsset<JsonBalanceExtended | JsonBalanceHistoryExtended>({
            source: children,
            token,
            selection: groupMap ? selection : [],
            key: 'volume',
            isSmallBalancesVisible,
            excludeFutures,
          });

          // Priced over the same accounts as `volume`: with the raw selection, a group none of
          // whose accounts is selected averaged an empty set and its USD value became NaN.
          const lastPrice = getPricePerAsset<JsonBalanceExtended | JsonBalanceHistoryExtended>({
            key: 'lastPrice',
            source: children,
            token,
            selection: groupMap ? selection : [],
            isSmallBalancesVisible,
          });

          return {
            token,
            lastPrice,
            volume,
            volumeUSD: volume * lastPrice,
          };
        }),
        totalVolumeUSD: getTotalValue(children, groupMap ? selection : []),
        totalVolumeTooltip: getTotalTooltip(
          children.filter((c): c is JsonBalanceExtended => 'totalVolumeUSD' in c),
          groupMap ? selection : [],
        ),
      };

      // we always want subtotal to be the last child of the group
      // so we can rely on it when calculating summary row
      const childrenWithSubtotal = [...children, subtotal];

      return {
        key: `group:${group}`,
        label: group,
        group,
        assets: [],
        children: childrenWithSubtotal,
      };
    },
  );
  // ensure the mapped result is typed for the sorter
  return getSortedBalanceAccountByGroup(balanceTreeData as SortableBalanceItem[]);
}
